import { offlineDb, type MutationQueueItem } from './db'
import { v4 as uuidv4 } from 'uuid'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'

export type EnqueueItem = Omit<MutationQueueItem, 'id' | 'clientId' | 'createdAt' | 'retryCount'>

async function addToQueue(item: EnqueueItem): Promise<void> {
  await offlineDb.mutationQueue.add({
    ...item,
    clientId: uuidv4(),
    createdAt: Date.now(),
    retryCount: 0,
  })
}

// Note: a completion carries no entity id, so enqueue cannot recognise a second one as the same
// change and would queue it twice - completing the shift twice on the server. Callers check first.
export async function hasQueuedCompletion(shiftId: number): Promise<boolean> {
  const queued = await offlineDb.mutationQueue.where('entity').equals('completeShift').toArray()
  return queued.some(item => item.shiftId === shiftId)
}

// Note: the queued change for a row, if there is one. The queue holds the last values this device
// wrote, which is what a cached aggregate was last adjusted by - so it is the honest baseline for the
// next adjustment, and unlike a value captured in a render closure it cannot go stale.
export async function queuedItemFor(
  entity: MutationQueueItem['entity'],
  entityId: number | undefined,
): Promise<MutationQueueItem | undefined> {
  if (entityId === undefined) return undefined

  const matches = await offlineDb.mutationQueue
    .where('[entity+entityId]')
    .equals([entity, entityId])
    .toArray()

  return matches[0]
}

export async function enqueue(incoming: EnqueueItem): Promise<void> {
  // Note: normalise the payload with convertDatesToUTC (the same converter the online ajax uses)
  // before it reaches IndexedDB. This does two things: (1) it deep-copies into plain objects, so a
  // Luxon DateTime isn't stored raw and then replayed as a bare {_zone, c, loc, ...} object the API
  // rejects (a 400 that aborts the whole sync batch); and (2) it converts every DateTime to a UTC
  // ISO string, exactly like an online request - without this, offline dates kept their mine-tz
  // offset and the server stored them shifted, e.g. a stoppage's startTime jumping hours (breaking
  // Down Hrs). Matching the online path keeps offline and online byte-identical.
  if (incoming.data !== undefined) {
    incoming = { ...incoming, data: convertDatesToUTC(incoming.data) }
  }

  await offlineDb.transaction('rw', offlineDb.mutationQueue, async () => {
    let existing: MutationQueueItem[] = []

    if (incoming.entityId !== undefined) {
      existing = await offlineDb.mutationQueue
        .where('[entity+entityId]')
        .equals([incoming.entity, incoming.entityId])
        .toArray()
    } else if (incoming.tempId) {
      existing = await offlineDb.mutationQueue
        .where('[entity+tempId]')
        .equals([incoming.entity, incoming.tempId])
        .toArray()
    }

    if (existing.length === 0) {
      await addToQueue(incoming)
      return
    }

    const prev = existing[0]

    if (incoming.operation === 'delete' && prev.operation === 'create') {
      await offlineDb.mutationQueue.delete(prev.id!)
      return
    }

    if (incoming.operation === 'update' && prev.operation === 'create') {
      await offlineDb.mutationQueue.update(prev.id!, { data: incoming.data })
      return
    }

    if (incoming.operation === 'update' && prev.operation === 'update') {
      await offlineDb.mutationQueue.update(prev.id!, {
        clientId: uuidv4(),
        data: incoming.data,
        rowVersion: incoming.rowVersion,
      })
      return
    }

    if (incoming.operation === 'delete' && prev.operation === 'update') {
      await offlineDb.mutationQueue.delete(prev.id!)
      await addToQueue(incoming)
      return
    }

    await addToQueue(incoming)
  })
}
